/**
 * NetworkResilience Service
 * 
 * Provides robust network operations with retry logic, exponential backoff,
 * connectivity monitoring, and offline request queuing.
 */

import { parseBackendError, isRetryableError, ERROR_TYPES } from './errorHandling';

/**
 * Default configuration for network resilience
 */
const DEFAULT_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffMultiplier: 2,
  jitterFactor: 0.1, // 10% jitter
  timeout: 30000, // 30 seconds
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryableErrorTypes: [
    ERROR_TYPES.NETWORK_ERROR,
    ERROR_TYPES.CONNECTION_TIMEOUT,
    ERROR_TYPES.SERVER_ERROR,
    ERROR_TYPES.SERVER_UNAVAILABLE,
    ERROR_TYPES.PROCESSING_ERROR
  ]
};

/**
 * NetworkResilience class for handling network operations with retry logic
 */
export class NetworkResilience {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.isOnline = navigator.onLine;
    this.offlineQueue = [];
    this.connectivityListeners = new Set();
    this.setupConnectivityMonitoring();
  }

  /**
   * Setup network connectivity monitoring
   */
  setupConnectivityMonitoring() {
    const handleOnline = () => {
      this.isOnline = true;
      this.notifyConnectivityChange(true);
      this.processOfflineQueue();
    };

    const handleOffline = () => {
      this.isOnline = false;
      this.notifyConnectivityChange(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Store references for cleanup
    this.cleanupConnectivity = () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }

  /**
   * Add connectivity change listener
   * @param {Function} listener - Callback function (isOnline) => void
   */
  addConnectivityListener(listener) {
    this.connectivityListeners.add(listener);
    return () => this.connectivityListeners.delete(listener);
  }

  /**
   * Notify all connectivity listeners
   * @param {boolean} isOnline - Current connectivity status
   */
  notifyConnectivityChange(isOnline) {
    this.connectivityListeners.forEach(listener => {
      try {
        listener(isOnline);
      } catch (error) {
        console.error('Error in connectivity listener:', error);
      }
    });
  }

  /**
   * Execute operation with retry logic and exponential backoff
   * @param {Function} operation - Async function to execute
   * @param {Object} options - Retry options
   * @returns {Promise} Result of the operation
   */
  async retryWithBackoff(operation, options = {}) {
    const config = { ...this.config, ...options };
    let lastError = null;
    let attempt = 0;

    // Check if we're offline and should queue the request
    if (!this.isOnline && config.queueWhenOffline !== false) {
      return this.queueOfflineRequest(operation, config);
    }

    while (attempt <= config.maxRetries) {
      try {
        // Add timeout wrapper if specified
        const operationWithTimeout = config.timeout 
          ? this.withTimeout(operation, config.timeout)
          : operation;

        const result = await operationWithTimeout();
        
        // Success - reset any error state
        if (attempt > 0) {
          console.log(`Operation succeeded after ${attempt} retries`);
        }
        
        return result;
      } catch (error) {
        lastError = error;
        attempt++;

        // Parse the error to determine if it's retryable
        const parsedError = parseBackendError(error, { 
          operation: config.operationName || 'network_operation',
          retryCount: attempt - 1
        });

        // Check if we should retry
        if (attempt > config.maxRetries || !this.shouldRetry(parsedError, config)) {
          console.error(`Operation failed after ${attempt - 1} retries:`, parsedError);
          throw parsedError;
        }

        // Calculate delay with exponential backoff and jitter
        const delay = this.calculateDelay(attempt - 1, config);
        
        console.warn(`Operation failed (attempt ${attempt}/${config.maxRetries + 1}), retrying in ${delay}ms:`, parsedError.message);

        // Wait before retrying
        await this.delay(delay);

        // Check if we went offline during the delay
        if (!this.isOnline && config.queueWhenOffline !== false) {
          return this.queueOfflineRequest(operation, config);
        }
      }
    }

    // This should never be reached, but just in case
    throw lastError;
  }

  /**
   * Determine if an error should trigger a retry
   * @param {Object} error - Parsed error object
   * @param {Object} config - Configuration options
   * @returns {boolean} Whether to retry
   */
  shouldRetry(error, config) {
    // Check if error type is retryable
    if (config.retryableErrorTypes.includes(error.type)) {
      return true;
    }

    // Check if HTTP status code is retryable
    if (error.context?.status && config.retryableStatusCodes.includes(error.context.status)) {
      return true;
    }

    // Use the error handling system's retry logic
    return isRetryableError(error);
  }

  /**
   * Calculate delay with exponential backoff and jitter
   * @param {number} attempt - Current attempt number (0-based)
   * @param {Object} config - Configuration options
   * @returns {number} Delay in milliseconds
   */
  calculateDelay(attempt, config) {
    // Exponential backoff: baseDelay * (backoffMultiplier ^ attempt)
    let delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);
    
    // Cap at maximum delay
    delay = Math.min(delay, config.maxDelay);
    
    // Add jitter to prevent thundering herd
    const jitter = delay * config.jitterFactor * (Math.random() - 0.5) * 2;
    delay += jitter;
    
    // Ensure delay is positive
    return Math.max(delay, 0);
  }

  /**
   * Add timeout wrapper to an operation
   * @param {Function} operation - Async operation
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Function} Operation with timeout
   */
  withTimeout(operation, timeout) {
    return async () => {
      return Promise.race([
        operation(),
        new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Operation timed out after ${timeout}ms`));
          }, timeout);
        })
      ]);
    };
  }

  /**
   * Queue request for when connectivity is restored
   * @param {Function} operation - Operation to queue
   * @param {Object} config - Configuration options
   * @returns {Promise} Promise that resolves when operation completes
   */
  queueOfflineRequest(operation, config) {
    return new Promise((resolve, reject) => {
      const queuedRequest = {
        operation,
        config,
        resolve,
        reject,
        timestamp: Date.now(),
        id: Math.random().toString(36).substr(2, 9)
      };

      this.offlineQueue.push(queuedRequest);
      
      console.log(`Queued offline request (${queuedRequest.id}). Queue size: ${this.offlineQueue.length}`);

      // Optional: Limit queue size to prevent memory issues
      if (this.offlineQueue.length > 100) {
        const oldestRequest = this.offlineQueue.shift();
        oldestRequest.reject(new Error('Request expired in offline queue'));
      }
    });
  }

  /**
   * Process queued offline requests when connectivity is restored
   */
  async processOfflineQueue() {
    if (this.offlineQueue.length === 0) {
      return;
    }

    console.log(`Processing ${this.offlineQueue.length} queued offline requests`);

    // Process requests in batches to avoid overwhelming the server
    const batchSize = 3;
    const queue = [...this.offlineQueue];
    this.offlineQueue = [];

    for (let i = 0; i < queue.length; i += batchSize) {
      const batch = queue.slice(i, i + batchSize);
      
      // Process batch concurrently
      await Promise.allSettled(
        batch.map(async (queuedRequest) => {
          try {
            const result = await this.retryWithBackoff(
              queuedRequest.operation, 
              { ...queuedRequest.config, queueWhenOffline: false }
            );
            queuedRequest.resolve(result);
          } catch (error) {
            queuedRequest.reject(error);
          }
        })
      );

      // Small delay between batches
      if (i + batchSize < queue.length) {
        await this.delay(500);
      }
    }
  }

  /**
   * Clear the offline queue (useful for cleanup)
   * @param {string} reason - Reason for clearing
   */
  clearOfflineQueue(reason = 'Manual clear') {
    const count = this.offlineQueue.length;
    this.offlineQueue.forEach(request => {
      request.reject(new Error(`Request cancelled: ${reason}`));
    });
    this.offlineQueue = [];
    
    if (count > 0) {
      console.log(`Cleared ${count} queued offline requests: ${reason}`);
    }
  }

  /**
   * Get current network status and queue information
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      queueSize: this.offlineQueue.length,
      config: this.config
    };
  }

  /**
   * Utility method for creating delays
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.cleanupConnectivity) {
      this.cleanupConnectivity();
    }
    this.clearOfflineQueue('Service destroyed');
    this.connectivityListeners.clear();
  }
}

/**
 * Default instance for convenience
 */
export const networkResilience = new NetworkResilience();

/**
 * Convenience function for retrying operations
 * @param {Function} operation - Async operation to retry
 * @param {Object} options - Retry options
 * @returns {Promise} Result of the operation
 */
export const retryOperation = (operation, options = {}) => {
  return networkResilience.retryWithBackoff(operation, options);
};

/**
 * Convenience function for monitoring connectivity
 * @param {Function} listener - Connectivity change listener
 * @returns {Function} Cleanup function
 */
export const monitorConnectivity = (listener) => {
  return networkResilience.addConnectivityListener(listener);
};

/**
 * Get current network status
 * @returns {Object} Network status
 */
export const getNetworkStatus = () => {
  return networkResilience.getStatus();
};

/**
 * Specialized retry function for image operations
 * @param {Function} operation - Image operation to retry
 * @param {Object} options - Options specific to image operations
 * @returns {Promise} Result of the operation
 */
export const retryImageOperation = (operation, options = {}) => {
  const imageConfig = {
    maxRetries: 2, // Fewer retries for images
    baseDelay: 500, // Shorter initial delay
    maxDelay: 10000, // 10 seconds max
    timeout: 15000, // 15 second timeout
    operationName: 'image_operation',
    ...options
  };

  return networkResilience.retryWithBackoff(operation, imageConfig);
};

/**
 * Specialized retry function for upload operations
 * @param {Function} operation - Upload operation to retry
 * @param {Object} options - Options specific to upload operations
 * @returns {Promise} Result of the operation
 */
export const retryUploadOperation = (operation, options = {}) => {
  const uploadConfig = {
    maxRetries: 3,
    baseDelay: 2000, // Longer initial delay for uploads
    maxDelay: 60000, // 1 minute max
    timeout: 60000, // 1 minute timeout
    operationName: 'upload_operation',
    queueWhenOffline: true, // Always queue uploads when offline
    ...options
  };

  return networkResilience.retryWithBackoff(operation, uploadConfig);
};